// @djsem/shared — Unified Job Orchestrator (Prompt 30).
//
// One canonical DJSEMJob shape all long-running work maps to. Existing module job types
// (AI artwork, transcription, stem separation, media analysis, exports, etc.) keep their
// own payloads but expose them through this common lifecycle: status, progress, retries,
// artifacts, credits, rights confirmation. Doesn't delete existing job types (that would
// break every consumer) — it provides the shared vocabulary they map onto.
//
// This module lives in @djsem/shared because every runtime that runs work needs it (analysis
// worker, render worker, runners) and shared is the DAG sink (no @djsem/* deps).

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult } from "./validation";
import { fnv1a } from "./ids";
import { SeqCounter } from "./runtime";

// ---- job vocabulary ---------------------------------------------------------

/** The kind of long-running work a job represents. */
export type DJSEMJobType =
  | "media_analysis" | "transcription" | "semantic_analysis"
  | "stem_separation"
  | "ai_artwork_generation" | "ai_video_generation"
  | "render_export" | "rekordbox_export" | "show_pack_export" | "campaign_pack_export"
  | "marketplace_pack_validation"
  | "cleanup" | "system_maintenance";

/** Lifecycle status of a job. See {@link DJSEMJobOrchestrator} for the legal state machine. */
export type DJSEMJobStatus =
  | "created"
  | "awaiting_auth"
  | "awaiting_rights_confirmation"
  | "awaiting_credits"
  | "queued"
  | "running"
  | "paused"
  | "completed"
  | "completed_with_warnings"
  | "failed"
  | "cancelled"
  | "expired";

// Legal transitions. Prevents modules from cooking up "queued → completed" without a
// running state or "cancelled → running" resurrection. Enforced by validateTransition.
const ALLOWED_TRANSITIONS: Record<DJSEMJobStatus, DJSEMJobStatus[]> = {
  created: ["awaiting_auth", "awaiting_rights_confirmation", "awaiting_credits", "queued", "failed", "cancelled"],
  awaiting_auth: ["awaiting_rights_confirmation", "awaiting_credits", "queued", "failed", "cancelled"],
  awaiting_rights_confirmation: ["awaiting_credits", "queued", "failed", "cancelled"],
  awaiting_credits: ["queued", "failed", "cancelled"],
  queued: ["running", "cancelled", "expired", "failed"],
  running: ["paused", "completed", "completed_with_warnings", "failed", "cancelled"],
  paused: ["running", "cancelled", "failed"],
  completed: [],
  completed_with_warnings: [],
  failed: ["queued"], // retry path
  cancelled: [],
  expired: [],
};

// ---- contracts --------------------------------------------------------------

/** The canonical job record shape all long-running DJSEM work maps onto. */
export interface DJSEMJob {
  id: string;
  organisationId?: string;
  projectId?: string;
  createdBy?: string;
  jobType: DJSEMJobType;
  status: DJSEMJobStatus;
  priority: number;
  progress: number; // 0..1
  payloadRef?: string;
  payload: unknown;
  estimatedCreditCost: number;
  chargedCreditCost?: number;
  outputArtifactIds: string[];
  warnings: string[];
  errors: string[];
  retryCount: number;
  maxRetries: number;
  createdAt: string;
  queuedAt?: string;
  startedAt?: string;
  completedAt?: string;
  expiresAt?: string;
}

/** The MIME/content kind of a job's output artifact. */
export type DJSEMJobArtifactType =
  | "json" | "audio" | "stem" | "image" | "video"
  | "xml" | "manifest" | "log" | "report";

/** An output artifact produced by a completed job. */
export interface DJSEMJobArtifact {
  id: string;
  jobId: string;
  artifactType: DJSEMJobArtifactType;
  ref: string;
  hash?: string;
  sizeBytes?: number;
  createdAt: string;
  warnings: string[];
}

// ---- inputs -----------------------------------------------------------------

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

/** Input to {@link DJSEMJobOrchestrator.createJob}. */
export interface CreateJobInput {
  id?: string;
  organisationId?: string; projectId?: string; createdBy?: string;
  jobType: DJSEMJobType;
  priority?: number;
  payload: unknown;
  payloadRef?: string;
  estimatedCreditCost?: number;
  maxRetries?: number;
  expiresAt?: string;
  createdAt?: string;
}
/** Input to {@link DJSEMJobOrchestrator.enqueueJob}. */
export interface EnqueueJobInput { job: DJSEMJob; queuedAt?: string; }
/** Input to {@link DJSEMJobOrchestrator.startJob}. */
export interface StartJobInput { job: DJSEMJob; startedAt?: string; }
/** Input to {@link DJSEMJobOrchestrator.updateProgress}. */
export interface UpdateJobProgressInput { job: DJSEMJob; progress: number; }
/** Input to {@link DJSEMJobOrchestrator.completeJob}. */
export interface CompleteJobInput { job: DJSEMJob; artifactIds?: string[]; chargedCreditCost?: number; warnings?: string[]; completedAt?: string; }
/** Input to {@link DJSEMJobOrchestrator.failJob}. */
export interface FailJobInput { job: DJSEMJob; error: string; failedAt?: string; }
/** Input to {@link DJSEMJobOrchestrator.cancelJob}. */
export interface CancelJobInput { job: DJSEMJob; reason?: string; cancelledAt?: string; }
/** Input to {@link DJSEMJobOrchestrator.retryJob}. */
export interface RetryJobInput { job: DJSEMJob; queuedAt?: string; }
/** Input to {@link DJSEMJobOrchestrator.attachArtifact}. */
export interface AttachJobArtifactInput { job: DJSEMJob; artifact: DJSEMJobArtifact; }

// Policy service inputs.
/** Input to {@link DJSEMJobPolicyService.requireRightsConfirmation} and {@link DJSEMJobPolicyService.requireCredits}. */
export interface JobPolicyInput {
  job: DJSEMJob;
  /** true when the user has confirmed a rights warning attached to the payload. */
  rightsConfirmed?: boolean;
  /** creditsRemaining is the org's current balance (from entitlement service). */
  creditsRemaining?: number;
}
/** Input to {@link DJSEMJobPolicyService.estimateCost}. */
export interface EstimateJobCostInput { jobType: DJSEMJobType; payload: unknown; }
/** Input to {@link DJSEMJobPolicyService.canRetry}. */
export interface CanRetryJobInput { job: DJSEMJob; }

// ---- orchestrator -----------------------------------------------------------

/**
 * Canonical job lifecycle orchestrator: creates jobs, validates their shape and status
 * transitions, and moves them through the queued → running → completed/failed/cancelled
 * state machine (see `ALLOWED_TRANSITIONS`).
 */
export class DJSEMJobOrchestrator {
  /**
   * Creates a new job in `"created"` status with a deterministic content-derived id.
   * @param input - The job type, payload, and optional org/project/priority/cost/retry fields.
   * @returns The new job.
   */
  createJob(input: CreateJobInput): DJSEMJob {
    return {
      id: input.id ?? `job_${input.jobType}_${_seq.next()}_${fnv1a(String(input.createdAt ?? AT))}`,
      organisationId: input.organisationId, projectId: input.projectId, createdBy: input.createdBy,
      jobType: input.jobType,
      status: "created",
      priority: input.priority ?? 5,
      progress: 0,
      payload: input.payload,
      payloadRef: input.payloadRef,
      estimatedCreditCost: input.estimatedCreditCost ?? 0,
      outputArtifactIds: [],
      warnings: [], errors: [],
      retryCount: 0,
      maxRetries: input.maxRetries ?? 3,
      createdAt: input.createdAt ?? AT,
      expiresAt: input.expiresAt,
    };
  }

  /**
   * Validates a job's structural invariants (required id/jobType, progress in 0..1,
   * retryCount within bounds).
   * @param job - The job to validate.
   * @returns The validation result.
   */
  validateJob(job: DJSEMJob): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!job.id) issues.push({ path: "$.id", message: "required" });
    if (!job.jobType) issues.push({ path: "$.jobType", message: "required" });
    if (job.progress < 0 || job.progress > 1) issues.push({ path: "$.progress", message: "must be 0..1" });
    if (job.retryCount < 0) issues.push({ path: "$.retryCount", message: "must be >= 0" });
    if (job.retryCount > job.maxRetries) issues.push({ path: "$.retryCount", message: "exceeds maxRetries" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "djsem_job");
  }

  /**
   * Checks whether a status transition is legal (see `ALLOWED_TRANSITIONS`). Rejects illegal
   * transitions — e.g. `'queued' → 'completed'` skipping `'running'` — with a clear error
   * instead of an inconsistent job record.
   * @param from - The current status.
   * @param to - The proposed next status.
   * @returns The validation result.
   */
  validateTransition(from: DJSEMJobStatus, to: DJSEMJobStatus): DJSEMValidationResult {
    const allowed = ALLOWED_TRANSITIONS[from] ?? [];
    if (allowed.includes(to)) return toDJSEMValidationResult({ valid: true, issues: [] }, "job_transition");
    return toDJSEMValidationResult({ valid: false, issues: [{ path: "$.status", message: `illegal transition ${from} → ${to}` }] }, "job_transition");
  }

  /**
   * Moves a job to `"queued"`.
   * @param input - The job and optional queued timestamp.
   * @returns The updated job.
   * @throws If the current status cannot legally transition to `"queued"`.
   */
  enqueueJob(input: EnqueueJobInput): DJSEMJob {
    const v = this.validateTransition(input.job.status, "queued");
    if (!v.ok) throw new Error(v.issues[0].message);
    return { ...input.job, status: "queued", queuedAt: input.queuedAt ?? AT };
  }

  /**
   * Moves a job to `"running"`.
   * @param input - The job and optional started timestamp.
   * @returns The updated job.
   * @throws If the current status cannot legally transition to `"running"`.
   */
  startJob(input: StartJobInput): DJSEMJob {
    const v = this.validateTransition(input.job.status, "running");
    if (!v.ok) throw new Error(v.issues[0].message);
    return { ...input.job, status: "running", startedAt: input.startedAt ?? AT };
  }

  /**
   * Updates a job's progress, clamped to 0..1 and monotonically forward-only (a runner
   * reporting a lower or out-of-range value cannot regress or corrupt state).
   * @param input - The job and the reported progress value.
   * @returns The updated job.
   */
  updateProgress(input: UpdateJobProgressInput): DJSEMJob {
    // Clamp — a runner reporting 1.05 shouldn't corrupt state. Progress goes forward only.
    const p = Math.max(input.job.progress, Math.min(1, Math.max(0, input.progress)));
    return { ...input.job, progress: p };
  }

  /**
   * Completes a job. Jobs of an output-producing type must have at least one output
   * artifact attached; if none is present, the job is failed instead (via {@link failJob}).
   * Status is `"completed_with_warnings"` if any warnings were carried, otherwise `"completed"`.
   * @param input - The job, optional artifact ids/credit cost/warnings/completed timestamp.
   * @returns The completed (or, on missing-artifact, failed) job.
   * @throws If the resulting status transition is illegal for the job's current status.
   */
  completeJob(input: CompleteJobInput): DJSEMJob {
    // Jobs that produce output artifacts must attach at least one before completing.
    // Cleanup/system jobs are exempt (they produce no artifact).
    const isOutputProducing = input.job.jobType !== "cleanup" && input.job.jobType !== "system_maintenance";
    const artifactIds = input.artifactIds ?? input.job.outputArtifactIds;
    if (isOutputProducing && artifactIds.length === 0) {
      return this.failJob({ job: input.job, error: "Job completed without output artifact.", failedAt: input.completedAt });
    }
    const warnings = [...input.job.warnings, ...(input.warnings ?? [])];
    const status: DJSEMJobStatus = warnings.length > 0 ? "completed_with_warnings" : "completed";
    const v = this.validateTransition(input.job.status, status);
    if (!v.ok) throw new Error(v.issues[0].message);
    return {
      ...input.job, status, progress: 1,
      outputArtifactIds: artifactIds, warnings,
      chargedCreditCost: input.chargedCreditCost ?? input.job.estimatedCreditCost,
      completedAt: input.completedAt ?? AT,
    };
  }

  /**
   * Fails a job, appending the error message. `failed` is reachable from most active states —
   * no strict transition check here so failures never bounce back as "illegal transition" errors.
   * @param input - The job, the error message, and an optional failed timestamp.
   * @returns The failed job.
   */
  failJob(input: FailJobInput): DJSEMJob {
    // failed is reachable from most active states — no strict transition check here so
    // failures never bounce back to the caller as "illegal transition" errors.
    return { ...input.job, status: "failed", errors: [...input.job.errors, input.error], completedAt: input.failedAt ?? AT };
  }

  /**
   * Cancels a job.
   * @param input - The job, an optional cancellation reason, and an optional cancelled timestamp.
   * @returns The cancelled job.
   * @throws If the current status cannot legally transition to `"cancelled"`.
   */
  cancelJob(input: CancelJobInput): DJSEMJob {
    const v = this.validateTransition(input.job.status, "cancelled");
    if (!v.ok) throw new Error(v.issues[0].message);
    const warnings = input.reason ? [...input.job.warnings, `Cancelled: ${input.reason}`] : input.job.warnings;
    return { ...input.job, status: "cancelled", warnings, completedAt: input.cancelledAt ?? AT };
  }

  /**
   * Requeues a failed job for retry, incrementing `retryCount` and resetting progress/timing
   * while preserving the original payload and error history.
   * @param input - The job and an optional re-queued timestamp.
   * @returns The re-queued job.
   * @throws If the job is not currently `"failed"`, or the retry limit has been reached.
   */
  retryJob(input: RetryJobInput): DJSEMJob {
    if (input.job.status !== "failed") throw new Error(`Cannot retry job in status '${input.job.status}'.`);
    if (input.job.retryCount >= input.job.maxRetries) throw new Error(`Retry limit reached (${input.job.maxRetries}).`);
    return {
      ...input.job, status: "queued", queuedAt: input.queuedAt ?? AT,
      retryCount: input.job.retryCount + 1,
      // Preserve original payload + errors (as history) — never destroy the failure trace.
      startedAt: undefined, completedAt: undefined, progress: 0,
    };
  }

  /**
   * Attaches an output artifact id to a job (idempotent — attaching the same artifact twice
   * is a no-op).
   * @param input - The job and the artifact to attach.
   * @returns The updated job.
   * @throws If the artifact's `jobId` does not match the job's `id`.
   */
  attachArtifact(input: AttachJobArtifactInput): DJSEMJob {
    if (input.artifact.jobId !== input.job.id) throw new Error("Artifact jobId does not match job.id.");
    if (input.job.outputArtifactIds.includes(input.artifact.id)) return input.job;
    return { ...input.job, outputArtifactIds: [...input.job.outputArtifactIds, input.artifact.id] };
  }
}

// ---- policy service ---------------------------------------------------------

// Structural cost defaults — NOT pricing. Modules override via estimateJobCost for their
// payloads; this is the fallback when a caller doesn't estimate first.
const DEFAULT_JOB_COST: Partial<Record<DJSEMJobType, number>> = {
  media_analysis: 1, transcription: 2, semantic_analysis: 1,
  stem_separation: 8, ai_artwork_generation: 4, ai_video_generation: 20,
  render_export: 3, rekordbox_export: 0, show_pack_export: 1, campaign_pack_export: 3,
  marketplace_pack_validation: 1, cleanup: 0, system_maintenance: 0,
};

/** Policy checks gating job progression: rights confirmation, credit balance, and retry eligibility. */
export class DJSEMJobPolicyService {
  /**
   * Requires rights confirmation for rights-sensitive job types (AI generation or export of
   * user content). Jobs of other types are always ok.
   * @param input - The job and whether rights have been confirmed.
   * @returns The validation result.
   */
  requireRightsConfirmation(input: JobPolicyInput): DJSEMValidationResult {
    // Jobs where user rights matter: any AI generation or export of user content.
    const rightsSensitive: DJSEMJobType[] = ["ai_artwork_generation", "ai_video_generation", "stem_separation", "render_export", "rekordbox_export", "show_pack_export", "campaign_pack_export", "marketplace_pack_validation"];
    if (!rightsSensitive.includes(input.job.jobType)) return toDJSEMValidationResult({ valid: true, issues: [] }, "rights_ok");
    if (input.rightsConfirmed) return toDJSEMValidationResult({ valid: true, issues: [] }, "rights_ok");
    return toDJSEMValidationResult({ valid: false, issues: [{ path: "$.rightsConfirmed", message: `${input.job.jobType} requires rights confirmation` }] }, "rights_required");
  }

  /**
   * Requires the org to have sufficient remaining credits for the job's estimated cost. If
   * the caller doesn't know the remaining balance, the check passes (unknown is not a denial).
   * @param input - The job and the org's current remaining credit balance (if known).
   * @returns The validation result.
   */
  requireCredits(input: JobPolicyInput): DJSEMValidationResult {
    if (input.creditsRemaining === undefined) return toDJSEMValidationResult({ valid: true, issues: [] }, "credits_unknown_ok");
    if (input.creditsRemaining >= input.job.estimatedCreditCost) return toDJSEMValidationResult({ valid: true, issues: [] }, "credits_ok");
    return toDJSEMValidationResult({ valid: false, issues: [{ path: "$", message: `Insufficient credits: ${input.creditsRemaining}/${input.job.estimatedCreditCost}` }] }, "credits_low");
  }

  /**
   * Estimates the structural (non-pricing) credit cost of a job type, from `DEFAULT_JOB_COST`.
   * @param input - The job type (and its payload, currently unused for estimation).
   * @returns The estimated cost, defaulting to 1 for unknown job types.
   */
  estimateCost(input: EstimateJobCostInput): number {
    return DEFAULT_JOB_COST[input.jobType] ?? 1;
  }

  /**
   * Determines whether a job is eligible for retry.
   * @param input - The job to check.
   * @returns True if the job is `"failed"` and hasn't exhausted its retry budget.
   */
  canRetry(input: CanRetryJobInput): boolean {
    return input.job.status === "failed" && input.job.retryCount < input.job.maxRetries;
  }
}

// ---- artifact factory -------------------------------------------------------

/** Input to {@link createJobArtifact}. */
export interface CreateJobArtifactInput {
  jobId: string;
  artifactType: DJSEMJobArtifactType;
  ref: string;
  hash?: string;
  sizeBytes?: number;
  warnings?: string[];
  createdAt?: string;
}

/**
 * Creates a new job artifact record with a deterministic content-derived id.
 * @param input - The job id, artifact type/ref, and optional hash/size/warnings/timestamp.
 * @returns The new artifact record.
 */
export function createJobArtifact(input: CreateJobArtifactInput): DJSEMJobArtifact {
  return {
    id: `art_${input.jobId}_${fnv1a(input.ref + (input.hash ?? ""))}`,
    jobId: input.jobId, artifactType: input.artifactType, ref: input.ref,
    hash: input.hash, sizeBytes: input.sizeBytes,
    warnings: input.warnings ?? [],
    createdAt: input.createdAt ?? AT,
  };
}
